import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { RightPanelProvider } from '../contexts/RightPanelContext';
import { ToastProvider } from '../contexts/ToastContext';
import { CommandPalette } from './CommandPalette';
import { NotificationOverlay } from './NotificationOverlay';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePreferences } from '../contexts/PreferencesContext';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/inbox': 'Inbox',
  '/meetings': 'Meetings',
  '/tasks': 'Tasks',
  '/projects': 'Projects',
  '/tags': 'Tags',
  '/people': 'People',
  '/views': 'Views',
  '/notes': 'Notes',
  '/search': 'Search',
  '/timeline': 'Timeline',
};

function getPageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/meetings/')) return 'Meeting';
  if (pathname.startsWith('/tasks/')) return 'Task';
  if (pathname.startsWith('/projects/')) return 'Project';
  if (pathname.startsWith('/people/')) return 'Person';
  if (pathname.startsWith('/views/')) return 'View';
  if (pathname.startsWith('/notes/')) return 'Note';
  return 'Dossier';
}

export function AppLayout() {
  const isMobile = useIsMobile();
  const { prefs } = usePreferences();
  const sidebarDefault = !isMobile && prefs['sidebar.defaultOpen'] !== 'false';
  const [sidebarOpen, setSidebarOpen] = useState(sidebarDefault);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const location = useLocation();

  // Auto-close sidebar on navigation when mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const title = getPageTitle(location.pathname);
    document.title = title === 'Dossier' ? 'Dossier' : `Dossier - ${title}`;
  }, [location.pathname]);

  // Global keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const tagName = target.tagName;
      const isEditing = tagName === 'INPUT' || tagName === 'TEXTAREA' || target.closest('.tiptap');

      // Ctrl+K always works (even in inputs) — standard search shortcut
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
        return;
      }

      // Skip other shortcuts when editing
      if (isEditing) return;

      // Ctrl+. — toggle quick capture
      if ((e.ctrlKey || e.metaKey) && e.key === '.') {
        e.preventDefault();
        setShowCapture(prev => !prev);
        return;
      }

      // Ctrl+/ — keyboard shortcuts overlay
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(prev => !prev);
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleToggleCapture = useCallback(() => {
    setShowCapture(prev => !prev);
  }, []);

  return (
    <ToastProvider>
      <RightPanelProvider>
        <div className="flex h-screen bg-bg-primary overflow-hidden">
          {/* Mobile backdrop */}
          {sidebarOpen && isMobile && (
            <div
              className="fixed inset-0 z-30 bg-black/50 sm:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
          <div className="flex flex-col flex-1 min-w-0">
            <Header
              sidebarOpen={sidebarOpen}
              onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              onSearchClick={() => setCommandPaletteOpen(true)}
              showCapture={showCapture}
              onToggleCapture={handleToggleCapture}
            />
            <main className="flex-1 overflow-hidden">
              <Outlet />
            </main>
          </div>
        </div>
        <CommandPalette
          open={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
        />
        {shortcutsOpen && <KeyboardShortcutsOverlay onClose={() => setShortcutsOpen(false)} />}
        <NotificationOverlay />
      </RightPanelProvider>
    </ToastProvider>
  );
}

function KeyboardShortcutsOverlay({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const shortcuts = [
    { keys: 'Ctrl+K', description: 'Open search / command palette' },
    { keys: 'Ctrl+.', description: 'Quick capture' },
    { keys: 'Ctrl+/', description: 'Show keyboard shortcuts' },
    { keys: 'Esc', description: 'Close overlay / popover' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-text-primary mb-4">Keyboard Shortcuts</h2>
        <div className="space-y-3">
          {shortcuts.map(s => (
            <div key={s.keys} className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">{s.description}</span>
              <kbd className="text-xs text-text-muted bg-bg-input px-2 py-1 rounded border border-border font-mono">
                {s.keys}
              </kbd>
            </div>
          ))}
        </div>
        <div className="mt-6 text-right">
          <button onClick={onClose} className="text-sm text-text-muted hover:text-text-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
